<template>
  <div class="check-block">
    <label class="check-row" :class="{ 'check-row--locked': locked }">
      <input
        type="checkbox"
        v-model="checked"
        :disabled="locked || busy"
      />
      <span><slot>{{ label }}</slot></span>
    </label>

    <p v-if="locked" class="check-lock">{{ lockedMessage }}</p>

    <div class="actions">
      <button
        type="button"
        class="chk-btn chk-btn--primary"
        :disabled="!checked || locked || busy"
        :aria-busy="busy"
        @click="confirm"
      >
        {{ busy ? 'Enregistrement…' : 'Confirmer mon acceptation' }}
      </button>
    </div>
  </div>
</template>

<script>
/**
 * Remplace SignatureTact (tracé manuscrit) par une simple case à cocher pour les
 * documents qui n'ont pas besoin d'une preuve de consentement individualisée
 * (CGU, documents informatifs). Émet le même événement `signature-saved` avec un
 * `image` (dataURL PNG) généré ici — un tampon "Accepté" horodaté — afin que le
 * pipeline backend existant (stamp sur le PDF, Signature model, fichier_signe)
 * n'ait rien à changer. Style volontairement plat (pas de carte/encadré) pour
 * ressembler à une case à cocher de formulaire classique.
 */
export default {
  name: 'CheckAcceptance',
  props: {
    documentName: { type: String, default: 'Document' },
    clientId: { type: [String, Number], default: '' },
    /** Texte affiché à côté de la case. Par défaut : accepte le document en cours. */
    label: {
      type: String,
      // Vue appelle default(rawProps) sans lier `this` : on lit documentName sur l'argument.
      default: (rawProps) => `J’ai lu et j’accepte le contenu de « ${rawProps.documentName || 'Document'} ».`,
    },
    locked: { type: Boolean, default: false },
    lockedMessage: {
      type: String,
      default: 'Parcourez le document jusqu’à la dernière page pour pouvoir valider.',
    },
    busy: { type: Boolean, default: false },
  },
  data() {
    return {
      checked: false,
    };
  },
  watch: {
    locked(isLocked) {
      if (isLocked) this.checked = false;
    },
  },
  methods: {
    buildStampImage() {
      const canvas = document.createElement('canvas');
      canvas.width = 600;
      canvas.height = 160;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#1d4ed8';
      ctx.font = 'bold 34px sans-serif';
      ctx.fillText('✓ Accepté', 20, 60);
      ctx.fillStyle = '#0f172a';
      ctx.font = '20px sans-serif';
      ctx.fillText(this.documentName, 20, 100);
      ctx.font = '16px sans-serif';
      ctx.fillText(new Date().toLocaleString('fr-FR'), 20, 130);
      return canvas.toDataURL('image/png');
    },
    confirm() {
      if (!this.checked) return;
      this.$emit('signature-saved', {
        image: this.buildStampImage(),
        client: this.clientId,
        ts: new Date().toISOString(),
      });
    },
  },
};
</script>

<style scoped>
.check-block {
  font-family: var(--font-body);
}

.check-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.95rem;
  color: var(--brand-navy, #01263e);
  cursor: pointer;
}

.check-row input {
  margin-top: 2px;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  accent-color: var(--brand-navy, #01263e);
}

.check-row--locked {
  cursor: not-allowed;
  opacity: 0.6;
}

.check-row :deep(a) {
  color: var(--brand-gold, #b8860b);
  font-weight: 600;
  text-decoration: underline;
}

.check-lock {
  margin: 8px 0 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--brand-brown, #543929);
}

.actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
}

.chk-btn {
  padding: 11px 20px;
  border-radius: var(--radius-pill, 999px);
  border: 1px solid transparent;
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.chk-btn--primary {
  background: var(--brand-navy);
  color: #fff;
}

.chk-btn--primary:hover:not(:disabled) {
  background: var(--brand-navy-deep, #0a1a2f);
}

.chk-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

@media (max-width: 560px) {
  .chk-btn {
    flex: 1 1 100%;
  }
}
</style>
